/**
 * Property Mapper Utility
 *
 * This utility provides comprehensive column property mappings for all tag types
 * based on predefined Excel titles mappings from tagsExcelTitles.js
 *
 * Used by:
 * - TagHarvester feature (ColumnMappingDialog)
 * - Masters feature (MasterImportWizard)
 */

import { log as debugLog } from '@/utils/debugLogger';

// Import Excel titles mappings from shared location
import {
  equipmentExcelTitlesMap,
  pipelineExcelTitlesMap,
  structuralExcelTitlesMap,
  inspectionExcelTitlesMap,
  anomalyExcelTitlesMap,
  annotationExcelTitlesMap,
  scaffoldExcelTitlesMap,
  permitExcelTitlesMap,
  containerExcelTitlesMap
} from './tagsExcelTitles';

/**
 * Tag type discriminators used in the database
 */
export const TAG_TYPES = {
  FORGE_EQUIPMENT: 'ForgeEquipment',
  EQUIPMENT: 'Equipment',
  FORGE_PIPELINE: 'ForgePipeline',
  PIPELINE: 'Pipeline',
  FORGE_STRUCTURAL: 'ForgeStructural',
  STRUCTURAL: 'Structural',
  FORGE_ANNOTATION: 'ForgeAnnotation',
  ANNOTATION: 'Annotation',
  FORGE_INSPECTION: 'ForgeInspection',
  INSPECTION: 'Inspection',
  FORGE_SCAFFOLD: 'ForgeScaffold',
  SCAFFOLD: 'Scaffold',
  // Permit and Container have no discriminator of their own: the whole family is stored as
  // ForgeScaffold and separated by the lowercase `type` field (forgeModels.js:2381-2389).
  // They still get their own entries here so they stop resolving through Scaffold's mapping.
  PERMIT: 'Permit',
  CONTAINER: 'Container',
  FORGE_ANOMALY: 'ForgeAnomaly',
  ANOMALY: 'Anomaly'
};

/**
 * Get the default Excel titles mapping for a specific tag type
 * @param {string} tagType - The tag discriminator (e.g., 'ForgeEquipment')
 * @returns {Object} Excel titles mapping object
 */
export function getDefaultExcelMapping(tagType) {
  switch (tagType) {
    case TAG_TYPES.EQUIPMENT:
    case TAG_TYPES.FORGE_EQUIPMENT:
      return equipmentExcelTitlesMap;
    case TAG_TYPES.PIPELINE:
    case TAG_TYPES.FORGE_PIPELINE:
      return pipelineExcelTitlesMap;
    case TAG_TYPES.STRUCTURAL:
    case TAG_TYPES.FORGE_STRUCTURAL:
      return structuralExcelTitlesMap;
    case TAG_TYPES.INSPECTION:
    case TAG_TYPES.FORGE_INSPECTION:
      return inspectionExcelTitlesMap;
    case TAG_TYPES.ANNOTATION:
    case TAG_TYPES.FORGE_ANNOTATION:
      return annotationExcelTitlesMap;
    case TAG_TYPES.SCAFFOLD:
    case TAG_TYPES.FORGE_SCAFFOLD:
      return scaffoldExcelTitlesMap;
    case TAG_TYPES.PERMIT:
      return permitExcelTitlesMap;
    case TAG_TYPES.CONTAINER:
      return containerExcelTitlesMap;
    case TAG_TYPES.ANOMALY:
    case TAG_TYPES.FORGE_ANOMALY:
      return anomalyExcelTitlesMap;
    default:
      debugLog('propertyMapper', 'Unknown tag type:', tagType);
      return {};
  }
}

/**
 * Get all available properties for a specific tag type from Excel titles mappings + special properties
 * @param {string} tagType - The tag discriminator (e.g., 'ForgeEquipment')
 * @returns {Array} Array of property objects with { property, title }
 */
export function getAvailableProperties(tagType) {
  const excelMapping = getDefaultExcelMapping(tagType);
  const properties = [];

  // Create property objects from Excel mapping
  Object.entries(excelMapping).forEach(([excelTitle, propertyName]) => {
    if (propertyName) {
      // Check if we already have this property (avoid duplicates)
      if (!properties.find(p => p.property === propertyName)) {
        properties.push({
          property: propertyName,
          title: excelTitle // Use Excel title as the display title
        });
      }
    }
  });

  // Add special properties that are not in Excel mappings but are valid for all tag types
  const specialProperties = [
    { property: 'facilityName', title: 'Facility Name' },
    // Add other special properties here if needed in the future
  ];

  specialProperties.forEach(specialProp => {
    if (!properties.find(p => p.property === specialProp.property)) {
      properties.push(specialProp);
    }
  });

  return properties.sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Get all unique properties across all tag types from Excel titles mappings
 * @returns {Array} Array of all unique property objects with { property, title }
 */
export function getAllUniqueProperties() {
  const allProperties = new Map();

  // Get properties from all tag types
  Object.values(TAG_TYPES).forEach(tagType => {
    const properties = getAvailableProperties(tagType);
    properties.forEach(prop => {
      // Use property name as key to avoid duplicates, keep first title found
      if (!allProperties.has(prop.property)) {
        allProperties.set(prop.property, prop);
      }
    });
  });

  return Array.from(allProperties.values()).sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Additional mapping for common column name variations
 * Maps common Excel column names to their corresponding forge properties
 */
const COMMON_COLUMN_MAPPINGS = {
  // Tag/Name variations
  'tag no': 'name',
  'tag no.': 'name',
  'tag no.*': 'name',
  'tag number': 'name',
  'tag id': 'name',
  'equipment tag': 'name',
  'equipment no': 'name',
  'equipment number': 'name',
  'tag': 'name',

  // Manufacturer variations
  'mfr': 'manufacturer',
  'mfg': 'manufacturer',
  'make': 'manufacturer',
  'vendor': 'manufacturer',

  // Model variations
  'model': 'modelNo',
  'model number': 'modelNo',
  'model no': 'modelNo',
  'model no.': 'modelNo',

  // Serial variations
  'serial': 'serialNo',
  'serial number': 'serialNo',
  'serial no': 'serialNo',
  'serial no.': 'serialNo',
  's/n': 'serialNo',

  // Description variations
  'desc': 'description',
  'equipment desc': 'equipmentDescription',
  'equipment description': 'equipmentDescription',
  'equip desc': 'equipmentDescription',
  'equip description': 'equipmentDescription',

  // Location variations
  'loc': 'location',
  'position': 'location',
  'area': 'area',
  'unit': 'unit',
  'plant': 'plant',

  // System variations
  'sys': 'system',
  'subsys': 'subSystem',
  'sub system': 'subSystem',
  'sub-system': 'subSystem',

  // Created by variations
  'created by': 'createdBy',
  'author': 'createdBy',
  'created_by': 'createdBy',

  // P&ID variations
  'pid': 'p_Id',
  'p&id': 'p_Id',
  'p and id': 'p_Id',
  'piping id': 'p_Id'
};

/**
 * Create property mapping for Excel column headers (uses Excel titles + common variations as fallback)
 * @param {Array} headers - Array of Excel column headers
 * @param {string} tagType - The target tag type discriminator
 * @returns {Object} Mapping object { excelHeader: forgeProperty }
 */
export function createPropertyMapping(headers, tagType) {
  const mapping = {};
  const defaultExcelMapping = getDefaultExcelMapping(tagType);
  const availableProperties = getAvailableProperties(tagType);
  const availablePropertyNames = availableProperties.map(p => p.property);

  // First priority: Apply Excel titles mappings
  headers.forEach(header => {
    const excelMapping = defaultExcelMapping[header];
    if (excelMapping) {
      mapping[header] = excelMapping;
    }
  });

  // Second priority: Apply common column mappings for headers not already mapped
  headers.forEach(header => {
    if (!mapping[header]) {
      const normalizedHeader = header.trim().toLowerCase();
      const commonMapping = COMMON_COLUMN_MAPPINGS[normalizedHeader];

      if (commonMapping && availablePropertyNames.includes(commonMapping)) {
        mapping[header] = commonMapping;
      }
    }
  });

  return mapping;
}

/**
 * Get exact property matches for headers (prioritizes Excel titles, then common mappings)
 * @param {Array} headers - Array of Excel column headers
 * @param {string} tagType - The target tag type discriminator
 * @returns {Array} Array of exact matches
 */
export function getExactMatches(headers, tagType) {
  const availableProperties = getAvailableProperties(tagType);
  const availablePropertyNames = availableProperties.map(p => p.property);
  const defaultExcelMapping = getDefaultExcelMapping(tagType);
  const matches = [];

  headers.forEach(header => {
    const normalizedHeader = header.trim().toLowerCase();

    // First priority: Try Excel titles mappings
    const excelMapping = defaultExcelMapping[header];
    if (excelMapping) {
      // Find the corresponding property object to get the proper title
      const propertyObj = availableProperties.find(p => p.property === excelMapping);
      matches.push({
        header: header,
        property: excelMapping,
        title: propertyObj ? propertyObj.title : header,
        type: 'excel'
      });
      return; // Found match, skip other checks
    }

    // Second priority: Try common mappings
    const commonMapping = COMMON_COLUMN_MAPPINGS[normalizedHeader];

    if (commonMapping && availablePropertyNames.includes(commonMapping)) {
      // Find the corresponding property object to get the proper title
      const propertyObj = availableProperties.find(p => p.property === commonMapping);
      matches.push({
        header: header,
        property: commonMapping,
        title: propertyObj ? propertyObj.title : header,
        type: 'common'
      });
    }
  });

  return matches;
}

/**
 * Validate property mapping against Excel titles mappings
 * @param {Object} mapping - The property mapping object
 * @param {string} tagType - The target tag type discriminator
 * @returns {Object} Validation result with errors and warnings
 */
export function validatePropertyMapping(mapping, tagType) {
  const availableProperties = getAvailableProperties(tagType);
  const availablePropertyNames = availableProperties.map(p => p.property);

  const result = {
    isValid: true,
    errors: [],
    warnings: [],
    unmappedHeaders: [],
    invalidMappings: []
  };

  Object.entries(mapping).forEach(([header, property]) => {
    if (property && !availablePropertyNames.includes(property)) {
      result.errors.push(`Invalid property "${property}" for header "${header}"`);
      result.invalidMappings.push({ header, property });
      result.isValid = false;
    }
  });

  // Check for unmapped headers
  Object.entries(mapping).forEach(([header, property]) => {
    if (!property) {
      result.unmappedHeaders.push(header);
      result.warnings.push(`Header "${header}" is not mapped to any property`);
    }
  });

  return result;
}

/**
 * Get property data type for validation (simplified to always return string)
 * @returns {string} The expected data type (always 'string' for now)
 */
export function getPropertyDataType() {
  return 'string';
}

/**
 * Export functions for use in components
 */
export default {
  TAG_TYPES,
  getAvailableProperties,
  getAllUniqueProperties,
  getDefaultExcelMapping,
  createPropertyMapping,
  getExactMatches,
  validatePropertyMapping,
  getPropertyDataType
};
